/**
 * The seven reads `/studio` is drawn from, in one place.
 *
 * `.docs/20-studio-portal-pages.md` §A names the panels and this names their
 * actions. Every one of them is `useStudioRead` — §DRY watchlist's first line,
 * "`useStudioAction` is the only way a studio page calls the API" — and every one of
 * them is **`inline`**, because a page of eleven panels must not go down because one
 * read failed. `Panel` is what renders the failure in place.
 *
 * **Only two of the seven take the period**, and that is the read models' shape
 * rather than a choice made here: `analytics_client_health` and
 * `analytics_effective_rate` are per-month, and MRR, revenue by model, tier mix,
 * package mix and pipeline each answer as of now. The period control therefore moves
 * the guardrail panels and nothing else, and the section heads say which month they
 * are about by printing the period the payload itself carries. The report lists this
 * as a gap: `analytics_revenue_by_model` in particular has a `current` month it
 * chooses for itself.
 *
 * `analytics_receivables` and `analytics_net_cash` are deliberately absent. They are
 * `/studio/money`'s, and DESIGN.md P2 gives every figure one owner — the past-due
 * figure `analytics.html` prints in its fourth tile is money's, and this page links
 * to it instead.
 */

import type { Period } from '@fareast/shared'

import { useStudioRead } from '../../../api/studio.js'

/** Every read is inline: a failed panel says so where it is, and the page stands. */
const INLINE = { inline: true } as const

export function useAnalytics(period: Period) {
  return {
    mrr: useStudioRead('analytics_mrr', {}, INLINE),
    revenue: useStudioRead('analytics_revenue_by_model', {}, INLINE),
    health: useStudioRead('analytics_client_health', { period }, INLINE),
    rate: useStudioRead('analytics_effective_rate', { period }, INLINE),
    tierMix: useStudioRead('analytics_tier_mix', {}, INLINE),
    packageMix: useStudioRead('analytics_package_mix', {}, INLINE),
    pipeline: useStudioRead('analytics_pipeline', {}, INLINE),
  }
}

export type AnalyticsReads = ReturnType<typeof useAnalytics>

/** The keys, as a list, so "have any of them answered" is one loop and not seven. */
export const READ_KEYS = [
  'mrr',
  'revenue',
  'health',
  'rate',
  'tierMix',
  'packageMix',
  'pipeline',
] as const

/**
 * Whether anything at all has come back — data or a refusal.
 *
 * The whole page waits on this and prints one muted line until it is true
 * (CONTRACT.md §2.14: "ONE muted line. No icon, no panel, no paragraph"). After it,
 * each panel waits on its own read, so a slow one is a line inside its own column
 * rather than a second full-page spinner.
 */
export function hasAnyAnswer(reads: AnalyticsReads): boolean {
  return READ_KEYS.some((key) => reads[key].data !== undefined || reads[key].error !== null)
}

/**
 * Whether anything **succeeded** — which is a different question, and it is the
 * Export button's.
 *
 * Seven refusals is an answer for the purpose of rendering (the page shows seven
 * failure lines rather than a loading line for ever) and is nothing at all for the
 * purpose of exporting: `analyticsSections` skips every read with no data, so the
 * button would have written a file containing a byte-order mark.
 */
export function hasAnyData(reads: AnalyticsReads): boolean {
  return READ_KEYS.some((key) => reads[key].data !== undefined)
}

/** The shape one read answers with, once it has. */
type Answer<K extends keyof AnalyticsReads> = NonNullable<AnalyticsReads[K]['data']>

export type MrrData = Answer<'mrr'>
export type RevenueData = Answer<'revenue'>
export type HealthData = Answer<'health'>
export type RateData = Answer<'rate'>
export type TierMixData = Answer<'tierMix'>
export type PackageMixData = Answer<'packageMix'>
export type PipelineData = Answer<'pipeline'>
